/**
 * AuditLogTable Component
 * Table displaying audit log entries with expandable rows
 */

import React from 'react';
import { AuditLogEntry } from '../../types/audit';
import { AuditLogRow } from './AuditLogRow';
import { LoadingSpinner } from '../LoadingSpinner';

interface AuditLogTableProps {
  logs: AuditLogEntry[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}

export const AuditLogTable: React.FC<AuditLogTableProps> = ({
  logs,
  loading,
  hasMore,
  onLoadMore,
}) => {
  if (loading && logs.length === 0) {
    return <LoadingSpinner message="Loading audit logs..." />;
  }

  if (logs.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        No audit logs found for the selected filters.
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Timestamp
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                User
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Action
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Details
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                IP Address
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {logs.map((entry) => (
              <AuditLogRow key={entry.auditId} entry={entry} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer with count and load more */}
      <div className="flex items-center justify-between mt-4 px-2">
        <div className="text-sm text-gray-500">
          Showing {logs.length} record{logs.length !== 1 ? 's' : ''}
        </div>

        {hasMore && (
          <button
            onClick={onLoadMore}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Loading...' : 'Load More'}
          </button>
        )}
      </div>
    </div>
  );
};
